import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  SupplierInstallationEmissionReportApiDto,
  SupplierInstallationEmissionReportCreatePayload,
  SupplierInstallationEmissionReportUpdatePayload,
} from '@artem-cbam-operator/common-server/supplier-installation-emission-report'
import { apiClient } from '@/shared/api/apiClient'
import { useSession } from '@/shared/hooks/useSession'

export type {
  SupplierInstallationEmissionReportApiDto as SupplierInstallationEmissionReportDto,
  SupplierInstallationEmissionReportCreatePayload,
  SupplierInstallationEmissionReportUpdatePayload,
}

export const supplierInstallationEmissionReportsQueryKey = (
  organizationId: string | undefined,
  supplierOrganizationId: string | undefined,
  supplierInstallationId: string | undefined
) =>
  [
    'supplier-installation-emission-reports',
    organizationId,
    supplierOrganizationId,
    supplierInstallationId,
  ] as const

const emissionReportsPath = (
  organizationId: string,
  supplierOrganizationId: string,
  supplierInstallationId: string
) =>
  `/v2/organizations/${organizationId}/supplier-organizations/${supplierOrganizationId}/supplier-installations/${supplierInstallationId}/emission-reports`

const emissionReportPath = (
  organizationId: string,
  supplierOrganizationId: string,
  supplierInstallationId: string,
  emissionReportId: string
) =>
  `${emissionReportsPath(organizationId, supplierOrganizationId, supplierInstallationId)}/${emissionReportId}`

/** Lists emission reports for a supplier installation. */
export const useSupplierInstallationEmissionReportsQuery = (
  supplierOrganizationId: string | undefined,
  supplierInstallationId: string | undefined
) => {
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  return useQuery({
    queryKey: supplierInstallationEmissionReportsQueryKey(
      organizationId,
      supplierOrganizationId,
      supplierInstallationId
    ),
    queryFn: async () => {
      const response = await apiClient.get<SupplierInstallationEmissionReportApiDto[]>(
        emissionReportsPath(
          organizationId,
          supplierOrganizationId!,
          supplierInstallationId!
        )
      )
      return response.data
    },
    enabled: Boolean(organizationId && supplierOrganizationId && supplierInstallationId),
  })
}

/** Creates and updates emission reports for a supplier installation. */
export const useSupplierInstallationEmissionReportMutations = (
  supplierOrganizationId: string | undefined,
  supplierInstallationId: string | undefined
) => {
  const queryClient = useQueryClient()
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: supplierInstallationEmissionReportsQueryKey(
        organizationId,
        supplierOrganizationId,
        supplierInstallationId
      ),
    })

  const createEmissionReport = useMutation({
    mutationFn: async (payload: SupplierInstallationEmissionReportCreatePayload) => {
      const response = await apiClient.post<SupplierInstallationEmissionReportApiDto>(
        emissionReportsPath(
          organizationId,
          supplierOrganizationId!,
          supplierInstallationId!
        ),
        payload
      )
      return response.data
    },
    onSuccess: invalidate,
  })

  const updateEmissionReport = useMutation({
    mutationFn: async ({
      emissionReportId,
      payload,
    }: {
      emissionReportId: string
      payload: SupplierInstallationEmissionReportUpdatePayload
    }) => {
      const response = await apiClient.patch<SupplierInstallationEmissionReportApiDto>(
        emissionReportPath(
          organizationId,
          supplierOrganizationId!,
          supplierInstallationId!,
          emissionReportId
        ),
        payload
      )
      return response.data
    },
    onSuccess: invalidate,
  })

  const deleteEmissionReport = useMutation({
    mutationFn: async (emissionReportId: string) => {
      await apiClient.delete(
        emissionReportPath(
          organizationId,
          supplierOrganizationId!,
          supplierInstallationId!,
          emissionReportId
        )
      )
    },
    onSuccess: invalidate,
  })

  return { createEmissionReport, updateEmissionReport, deleteEmissionReport }
}
